import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  useTheme,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import { DataGridCustomToolbarClientMode } from "components/DataGridCustomToolbar";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import {
  ClearOutlined,
  ModeEditOutlineOutlined,
} from "@mui/icons-material";
import LinearProgress from "@mui/material/LinearProgress";
import {
  GetRouteListByStation,
} from "api/routes";
import {
  DeletePackage,
  GetUnassignedPackageList,
  scanPackage,
} from "api/packages";
import Swal from "sweetalert2";
import {
  getScanText,
  playAlertSound,
  playBabySneezeSound,
  playCorrectSound,
  playIncorrectSound,
} from "utils";
import { isMobile } from "react-device-detect";
import FlexBetween from "components/FlexBetween";
import moment from "moment";
import { GetFinalStationList, GetStationDetailById } from "api/stations";
import useStationRefresh, { pickDefaultStation } from "hooks/useStationRefresh";
import PackageEditDialogForScan from "components/PackageEditDialogForScan";
import { ensureShippingFee } from "utils/shippingFeePrompt";
import { resolveScanStation } from "utils/resolveScanStation";
import i18n from "../../i18n";

const UnassginedPackage = () => {
  const theme = useTheme();

  // values to be sent to the backend
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [filteredRows, setFilteredRows] = useState(null);

  const [packageData, setPackageData] = useState(null); // State to hold package data for editing

  const [selectedStationForSearch, setSelectedStationForSearch] =
    useState(null);
  const [routeListForSearch, setRouteListForSearch] = useState([]);
  const [routeIdForSearch, setRouteIdForSearch] = useState("");
  const [stationListForSearch, setStationListForSearch] = useState([]);
  const [successCount] = useState(0);

  const { user: _user, defaultStation: _defaultStation, ready } = useStationRefresh();
  const isAgentUser = _user?.role === "agent";

  // values to set in DataGrid
  const [rows, setRows] = useState([]);
  const [, setTotalCount] = useState(0);

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  function RefreshPackageList(station, route) {
    setIsLoading(true);
    GetUnassignedPackageList(station, route).then((res) => {
      if (res.status === 200) {
        setRows(res.data.packages);
        setTotalCount(res.data.total);
      } else {
        setRows([]);
      }
      setIsLoading(false);
    });
  }

  useEffect(() => {
    if (selectedStationForSearch) {
      GetRouteListByStation(selectedStationForSearch.id).then((res) => {
        // Show only routes that pass through the selected station (empty if none).
        const routes = res.success && Array.isArray(res.data) ? res.data : [];
        setRouteListForSearch(routes);
        setRouteIdForSearch(routes[0]?.id || "");
      });
      RefreshPackageList(selectedStationForSearch.id, routeIdForSearch);
    }
  }, [selectedStationForSearch, routeIdForSearch, successCount]);



  useEffect(() => {
    setSelectedStationForSearch(_defaultStation);
    // Route list is driven entirely by the selected-station effect above
    // (GetRouteListByStation), so it only ever shows routes for that station.

    GetFinalStationList().then((res) => {
      if (res && res.success) {
        setStationListForSearch(res.data);
        setSelectedStationForSearch(pickDefaultStation(res.data, _defaultStation));
      } else {
        console.log(res?.message);
        setSelectedStationForSearch(_defaultStation);
      }
    });
    // Re-runs once the refreshed profile/default station lands (`ready`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  function filterSearch(dataArr, searchValue) {
    if (dataArr) {
      let newArr = dataArr.slice();
      if (searchValue) {
        newArr = newArr.filter((element) => {
          return (
            (element.tracking &&
              element.tracking
                .toLowerCase()
                .includes(searchValue.toLowerCase())) ||
            (element.description &&
              element.description
                .toLowerCase()
                .includes(searchValue.toLowerCase())) ||
            (element.customer &&
              element.customer.name
                .toLowerCase()
                .includes(searchValue.toLowerCase())) ||
            (element.orderId &&
              element.orderId.toLowerCase().includes(searchValue.toLowerCase()))
          );
        });
      }

      setFilteredRows(newArr);
    }
  }

  useEffect(() => {
    filterSearch(rows, search);
  }, [rows, search]);

  // Edit handler: pre-fill state and open edit dialog
  const buttonEditHandler = (rowValue) => {
    setPackageData(rowValue);
    setEditDialogOpen(true);
  };

  // Handle dialog close and clear package data
  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    // Clear packageData to ensure fresh state for next edit
    setPackageData(null);
  };

  const scanPackageSubmited = async (tracking, pageStation) => {
    // Assigning a customer can change the parcel's route (the customer's default route
    // wins), so scan at the next station of ITS route, not necessarily the selected one.
    const selectedStation = await resolveScanStation(tracking, pageStation);
    const stationChanged = selectedStation.id !== pageStation.id;
    // Same COD / Fanshine / sub-customer service-fee prompt the Scan page shows.
    if (!(await ensureShippingFee(tracking, selectedStation))) return;
    let actionType = "unassigned-auto-scan";
    scanPackage(getScanText(tracking), selectedStation.id, actionType).then(
      (res) => {
        if (res && res.success) {
          if (
            selectedStation.id === "VTE" &&
            res.data &&
            (res.data.routeId === "VTE-THK" || res.data.routeId === "VTE-DKC")
          ) {
            playBabySneezeSound(); // to indicate that the scanned item need to be sent to THK branch
          } else {
            playCorrectSound(); // to indicate that the scanned item need to be sent to PHT branch
          }
          Swal.fire({
            icon: "success",
            title: `${tracking}`,
            text: stationChanged
              ? i18n.t("scan.passToStation", { station: `${selectedStation.name} (${selectedStation.id})` })
              : `The package is matched and the automatic scan is complete!`,
            // The station changed → keep the message up until staff acknowledge it.
            ...(stationChanged ? {} : { timer: 1500 }),
          });
          setEditDialogOpen(false);
          setSearch("");
          RefreshPackageList(selectedStationForSearch.id, routeIdForSearch);
        } else if (res.status === 202) {
          console.log(selectedStation);
          if (!selectedStation.isSortStation) {
            playIncorrectSound();
            Swal.fire({
              title: res.data.status,
              icon: "warning",
              text: res.data.message,
              timer: 2000,
            });
            return;
          }
        } else {
          playIncorrectSound();
          // scanPackage() resolves with the response body, so status/message sit on `res`.
          Swal.fire({
            title: res?.status || "Error",
            icon: "error",
            text: res?.message,
            timer: 2000,
          });
        }
      }
    );
  };

  // Delete handler
  const buttonDeleteHandler = (event, cellValues) => {
    console.log("delete button clicked");
    Swal.fire({
      title: `Delete ${cellValues.row.tracking} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        DeletePackage(cellValues.row._id).then((res) => {
          if (res.status === 200) {
            // DELETE SELECTED ROW FROM ARRAY "data.packages"
            setRows(() => {
              return rows.filter((item) => item._id !== cellValues.id);
            });
            playCorrectSound();
            Swal.fire({
              title: "Deleted!",
              text: res.message,
              icon: "success",
              timer: 1500,
            });
          } else {
            playAlertSound();
            Swal.fire({
              backdrop: true,
              toast: false,
              icon: "error",
              text: res.message || "Failed to delete package",
              showConfirmButton: true,
            });
          }
        });
      }
    });
  };

  const columns = [
    {
      field: "_id",
      headerName: "ID",
      flex: 1,
      hide: true,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 0.5,
      valueGetter: (params) => params.row.customer && params.row.customer.name,
      hide: isMobile,
    },
    {
      field: "tracking",
      headerName: "Tracking",
      flex: 0.5,
    },
    {
      field: "orderId",
      headerName: "Order ID",
      flex: 0.5,
      hide: isMobile,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      hide: isMobile,
    },
    {
      field: "routeId",
      headerName: "Route ID",
      flex: 0.5,
      hide: isMobile,
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 0.3,
      hide: true,
    },
    // {
    //   field: "quantity",
    //   headerName: "QTY",
    //   flex: 0.5,
    // },

    {
      field: "status",
      headerName: "Status",
      flex: 0.3,
      hide: true,
    },
    {
      field: "station",
      headerName: "Station",
      flex: 0.3,
      hide: isMobile,
    },
    {
      field: "isCOD",
      headerName: "COD",
      flex: 0.3,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isCOD ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "shippingFee",
      headerName: "ຄ່າບໍລິການ",
      flex: 0.3,
    },
    {
      field: "finalShippingFee",
      headerName: "Final Fee",
      flex: 0.3,
      hide: isMobile,
    },
    {
      field: "isForwardRequired",
      headerName: "ສົ່ງຕໍ່",
      flex: 0.3,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isForwardRequired ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isDeliveryRequired",
      headerName: "ສົ່ງຢູ່ບ້ານ",
      flex: 0.3,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isDeliveryRequired ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isSelfOrder",
      headerName: "ສັ່ງເອງ",
      flex: 0.3,
      hide: isMobile,
      renderCell: (cellValues) => {
        return cellValues.row.isSelfOrder ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "isSubCustomer",
      headerName: "ລູກຄ້າຕົວແທນ",
      flex: 0.3,
      hide: isMobile || isAgentUser,
      renderCell: (cellValues) => {
        return cellValues.row.isSubCustomer ? (
          <CheckBoxIcon color="secondary" />
        ) : (
          <DoNotDisturbIcon color="primary" />
        );
      },
    },
    {
      field: "agentId",
      headerName: "ຕົວແທນ",
      flex: 0.3,
      hide: isMobile || isAgentUser,
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 0.7,
      hide: isMobile,
      valueGetter: (params) =>
        moment(params.row.updatedAt)
          .tz("Asia/Vientiane")
          .format("DD/MM/YYYY, h:mm:ss A"),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 0.7,
      valueGetter: (params) =>
        moment(params.row.createdAt)
          .tz("Asia/Vientiane")
          .format("DD/MM/YYYY, h:mm:ss A"),
    },

    {
      field: "edit",
      headerName: "Edit",
      flex: 0.2,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outline"
            onClick={() => {
              buttonEditHandler(cellValues.row);
            }}
            sx={{ "&:hover": { color: theme.palette.warning.main } }}
          >
            <ModeEditOutlineOutlined />
          </IconButton>
        );
      },
    },
    {
      field: "del",
      headerName: "Delete",
      flex: 0.2,
      hide: true,
      renderCell: (cellValues) => {
        return (
          <IconButton
            variant="outline"
            sx={{ "&:hover": { color: theme.palette.error.main } }}
            onClick={(event) => buttonDeleteHandler(event, cellValues)}
          >
            <ClearOutlined />
          </IconButton>
        );
      },
    },
  ];

  return (
    <PageShell
      title="Unassigned Package"
      subtitle="ກຳໜົດຂໍ້ມູນລູກຄ້າໃຫ້ພັດສະດຸທີ່ຍັງບໍ່ຮູ້ເຈົ້າຂອງສິນຄ້າເທື່ອ"
    >
      <BentoCard sx={{ mb: "22px" }} bodySx={{ p: "20px 22px" }}>
        <FlexBetween>
          {/* Scan package section */}
          <Grid container spacing={2}>
              <Grid item xl={3} md={6} xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select station</InputLabel>
                  <Select
                    label="Select station"
                    value={
                      selectedStationForSearch
                        ? selectedStationForSearch.id
                        : ""
                    }
                    onChange={(e) => {
                      GetStationDetailById(e.target.value).then((res) => {
                        if (res.success) {
                          setSelectedStationForSearch(res.data);
                        } else {
                          console.log(res.message);
                        }
                      });
                    }}
                    required
                  >
                    {stationListForSearch &&
                      stationListForSearch.map(({ id, name }) => {
                        return (
                          <MenuItem key={id} value={id}>
                            {id} ({name})
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xl={3} md={6} xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Route</InputLabel>
                  <Select
                    id="route"
                    value={!routeIdForSearch ? "" : routeIdForSearch}
                    label="Route"
                    onChange={(event) =>
                      setRouteIdForSearch(event.target.value)
                    }
                    required
                  >
                    {routeListForSearch &&
                      routeListForSearch.map(({ id, name }) => {
                        return (
                          <MenuItem key={id} value={id}>
                            {id} ({name})
                          </MenuItem>
                        );
                      })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
        </FlexBetween>
      </BentoCard>

      {isLoading ? (
        <Box sx={{ width: "100%", mb: 1 }}>
          <LinearProgress color="secondary" />
        </Box>
      ) : (
        ""
      )}
      <Box height="77vh">
        <StyledDataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={filteredRows || []}
          columns={columns}
          rowCount={filteredRows ? filteredRows.length : 0}
          rowsPerPageOptions={[20, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="client"
          sortingMode="client"
          filterMode="client"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          // onSortModelChange={(newSortModel) => setSort(...newSortModel)}
          components={{ Toolbar: DataGridCustomToolbarClientMode }}
          componentsProps={{
            toolbar: { search, setSearch },
          }}
          containerSx={{ height: "100%" }}
          autoHeight={false}
        />
      </Box>

      <PackageEditDialogForScan
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        packageData={packageData}
        onSuccess={()=> scanPackageSubmited(packageData.tracking, selectedStationForSearch)}
        selectedStation={selectedStationForSearch}
      />
    </PageShell>
  );
};

export default UnassginedPackage;
